import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Drawer, Typography, IconButton, Button, Chip, TextField,
  Checkbox, Collapse, CircularProgress, Divider, Tooltip,
  ToggleButton, ToggleButtonGroup, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DatePicker from 'react-datepicker';
import ProjectService from '../services/ProjectService';
import SecureImage from './SecureImage';
import imageCompression from 'browser-image-compression';
import { useToast } from '../contexts/ToastContext';

const SECTION_META = {
  entries:    { label: 'Time Entries',  emptyLabel: 'No time entries found' },
  expenses:   { label: 'Expenses',      emptyLabel: 'No expenses found' },
  subServices:{ label: 'Services',      emptyLabel: 'No services found' },
  inventory:  { label: 'Materials',     emptyLabel: 'No materials found' },
};

function EntryRow({ item, section, checked, onToggle, hidePrice = false }) {
  const label = item.description
    || item.name
    || (item.subService?.name ?? '')
    || (item.inventory?.name ?? item.customName ?? '—');

  const secondary = (() => {
    if (section === 'entries')
      return `${item.workingHours ?? 0} h · ${item.entryDate ?? ''}`;
    if (section === 'expenses')
      return hidePrice ? (item.description || '—') : `${item.price ?? ''} CHF`;
    if (section === 'subServices')
      return `${item.quantity ?? ''} ${item.subService?.unit ?? ''}`;
    if (section === 'inventory')
      return `${item.quantity ?? ''} ${item.inventory?.unit ?? item.customUnit ?? ''}`;
    return '';
  })();

  return (
    <Box
      onClick={() => onToggle(item.id)}
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1, px: 1.5, py: 1,
        cursor: 'pointer', borderRadius: 1.5, mb: 0.5,
        bgcolor: checked ? '#EFF6FF' : 'transparent',
        border: checked ? '1px solid #BFDBFE' : '1px solid transparent',
        '&:hover': { bgcolor: checked ? '#EFF6FF' : '#F8FAFC' },
      }}
    >
      <Checkbox
        checked={checked}
        size="small"
        sx={{ p: 0, pt: 0.2, color: '#94A3B8', '&.Mui-checked': { color: '#427898' } }}
        onClick={e => e.stopPropagation()}
        onChange={() => onToggle(item.id)}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#1E293B', lineHeight: 1.3 }}>
          {label}
        </Typography>
        {secondary && (
          <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 0.2 }}>{secondary}</Typography>
        )}
      </Box>
    </Box>
  );
}

function Section({ id, items, selectedIds, onToggle, open, onToggleOpen, filter, hidePrice = false }) {
  const meta = SECTION_META[id];
  const visible = filter
    ? items.filter(i => {
        const label = (i.description || i.name || i.subService?.name || i.inventory?.name || i.customName || '').toLowerCase();
        return label.includes(filter.toLowerCase());
      })
    : items;
  const checkedCount = items.filter(i => selectedIds.includes(i.id)).length;

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        onClick={onToggleOpen}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
          cursor: 'pointer', borderRadius: 1.5, bgcolor: '#F8FAFC',
          '&:hover': { bgcolor: '#EEF2F6' },
        }}
      >
        {open ? <ExpandLessIcon sx={{ fontSize: 17, color: '#64748B' }} /> : <ExpandMoreIcon sx={{ fontSize: 17, color: '#64748B' }} />}
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>
          {meta.label}
        </Typography>
        {checkedCount > 0 && (
          <Chip size="small" label={checkedCount} sx={{ height: 18, fontSize: 11, bgcolor: '#427898', color: '#fff', fontWeight: 700 }} />
        )}
      </Box>
      <Collapse in={open}>
        <Box sx={{ pt: 0.5, pl: 0.5 }}>
          {visible.length === 0
            ? <Typography sx={{ fontSize: 12, color: '#94A3B8', px: 1.5, py: 1 }}>{meta.emptyLabel}</Typography>
            : visible.map(item => (
                <EntryRow key={item.id} item={item} section={id}
                  checked={selectedIds.includes(item.id)}
                  onToggle={onToggle} hidePrice={hidePrice} />
              ))
          }
        </Box>
      </Collapse>
    </Box>
  );
}

export default function ReportCompositionDrawer({ open, onClose, project, editingReport, onSaved, entryUsers = [], fromField = false, currentUserId = null }) {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [type, setType] = useState('FIELD_SERVICE');
  const [workDescription, setWorkDescription] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filterUserId, setFilterUserId] = useState('');
  const [filterInvoiced, setFilterInvoiced] = useState('all');
  const [filter, setFilter] = useState('');
  const [sectionsOpen, setSectionsOpen] = useState({ entries: true, expenses: false, subServices: false, inventory: false });

  const [allEntries, setAllEntries] = useState([]);
  const [allExpenses, setAllExpenses] = useState([]);
  const [allSubServices, setAllSubServices] = useState([]);
  const [allInventory, setAllInventory] = useState([]);

  const [selectedEntryIds, setSelectedEntryIds] = useState([]);
  const [selectedExpenseIds, setSelectedExpenseIds] = useState([]);
  const [selectedSubServiceIds, setSelectedSubServiceIds] = useState([]);
  const [selectedInventoryIds, setSelectedInventoryIds] = useState([]);

  const [images, setImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [saving, setSaving] = useState(false);
  const [savedReportId, setSavedReportId] = useState(null);

  const reportId = editingReport?.id || savedReportId;

  // Load project data into entry lists — fetch directly so we get all items, not just the current page
  useEffect(() => {
    if (!open || !project?.id) return;
    const projectId = project.id;
    Promise.all([
      ProjectService.getProject(0, 1000, projectId),
      ProjectService.getProjectSubServices(projectId, 0, 1000),
      ProjectService.getInventoryUsageByProject(projectId),
    ]).then(([projData, subServicesData, materials]) => {
      setAllEntries(projData.projectEntries?.content || []);
      setAllExpenses(projData.projectExpenses?.content || []);
      setAllSubServices(subServicesData.content || []);
      setAllInventory(materials || []);
    }).catch(() => {});
  }, [open, project?.id]);

  // Populate form when editing an existing report
  useEffect(() => {
    if (open && editingReport) {
      setType(editingReport.type || 'FIELD_SERVICE');
      setWorkDescription(editingReport.workDescription || '');
      setSelectedEntryIds(editingReport.selectedEntryIds || []);
      setSelectedExpenseIds(editingReport.selectedExpenseIds || []);
      setSelectedSubServiceIds(editingReport.selectedSubServiceIds || []);
      setSelectedInventoryIds(editingReport.selectedInventoryIds || []);
      setSavedReportId(editingReport.id);
    } else if (open && !editingReport) {
      setType('FIELD_SERVICE');
      setWorkDescription('');
      setSelectedEntryIds([]);
      setSelectedExpenseIds([]);
      setSelectedSubServiceIds([]);
      setSelectedInventoryIds([]);
      setSavedReportId(null);
    }
    if (open) {
      setFilterUserId(fromField && currentUserId ? String(currentUserId) : '');
      setFilterInvoiced('all');
      setFilter('');
      setStartDate(null);
      setEndDate(null);
    }
  }, [open, editingReport]); // eslint-disable-line

  // Load images for existing report
  useEffect(() => {
    if (open && reportId) {
      ProjectService.getReportImages(reportId)
        .then(data => setImages(data || []))
        .catch(() => {});
    } else if (open && !editingReport) {
      setImages([]);
    }
  }, [open, reportId, editingReport]);

  // Filter entries by date range, employee (who logged it), and invoiced status
  const filteredEntries = allEntries.filter(e => {
    if (startDate || endDate) {
      const d = e.entryDate ? new Date(e.entryDate) : null;
      if (d) {
        if (startDate && d < startDate) return false;
        if (endDate && d > endDate) return false;
      }
    }
    if (filterUserId && String(e.createdBy) !== String(filterUserId)) return false;
    if (filterInvoiced === 'yes' && !e.invoiced) return false;
    if (filterInvoiced === 'no' && e.invoiced) return false;
    return true;
  });

  // Only show employees who actually have entries on this project
  const entryEmployees = entryUsers.length > 0
    ? entryUsers.filter(u => allEntries.some(e => String(e.createdBy) === String(u.id)))
    : [];

  function toggle(setter, id) {
    setter(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  async function handleSaveDraft() {
    setSaving(true);
    try {
      const dto = {
        reportId: reportId || null,
        type,
        workDescription,
        selectedEntryIds,
        selectedExpenseIds,
        selectedSubServiceIds,
        selectedInventoryIds,
      };
      const saved = await ProjectService.createOrUpdateReport(project.id, dto);
      setSavedReportId(saved.id);
      showToast('Report saved as draft', 'success');
      onSaved && onSaved(saved);
    } catch {
      showToast('Failed to save report', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handlePresentToCustomer() {
    setSaving(true);
    try {
      const dto = {
        reportId: reportId || null,
        type, workDescription,
        selectedEntryIds, selectedExpenseIds, selectedSubServiceIds, selectedInventoryIds,
      };
      const saved = await ProjectService.createOrUpdateReport(project.id, dto);
      setSavedReportId(saved.id);
      onSaved && onSaved(saved);
      onClose();
      if (fromField) {
        navigate(`/sign-report/${saved.id}`, { state: { from: '/field' } });
      } else {
        window.open(`/sign-report/${saved.id}`, '_blank');
      }
    } catch {
      showToast('Failed to save report', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish() {
    if (!reportId) {
      showToast('Save the report first', 'warning');
      return;
    }
    setSaving(true);
    try {
      const published = await ProjectService.publishReport(reportId);
      showToast('Report published', 'success');
      onSaved && onSaved(published);
      onClose();
    } catch {
      showToast('Failed to publish report', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleImageUpload(files) {
    if (!files || files.length === 0) return;
    if (!reportId) {
      // Save draft first to get an ID
      setSaving(true);
      try {
        const dto = { reportId: null, type, workDescription, selectedEntryIds, selectedExpenseIds, selectedSubServiceIds, selectedInventoryIds };
        const saved = await ProjectService.createOrUpdateReport(project.id, dto);
        setSavedReportId(saved.id);
        await uploadFilesToReport(saved.id, files);
      } catch {
        showToast('Failed to upload image', 'error');
      } finally {
        setSaving(false);
      }
    } else {
      await uploadFilesToReport(reportId, files);
    }
  }

  async function uploadFilesToReport(id, files) {
    setUploadingImage(true);
    for (const file of Array.from(files)) {
      try {
        const compressed = await imageCompression(file, { maxSizeMB: 0.3, maxWidthOrHeight: 1920 });
        const img = await ProjectService.uploadReportImage(id, compressed);
        setImages(prev => [...prev, img]);
      } catch {
        showToast('Failed to upload image', 'error');
      }
    }
    setUploadingImage(false);
  }

  async function handleDeleteImage(imageId) {
    try {
      await ProjectService.deleteReportImage(reportId, imageId);
      setImages(prev => prev.filter(i => i.id !== imageId));
    } catch {
      showToast('Failed to delete image', 'error');
    }
  }

  async function handleCaptionChange(imageId, caption) {
    try {
      await ProjectService.updateReportImageCaption(reportId, imageId, caption);
      setImages(prev => prev.map(i => i.id === imageId ? { ...i, caption } : i));
    } catch {
      showToast('Failed to update caption', 'error');
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 640 }, boxShadow: '-4px 0 32px rgba(15,23,42,0.12)', display: 'flex', flexDirection: 'column' } }}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.08)' } } }}
    >
      {/* Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #EEF2F6', display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#1E293B' }}>
            {editingReport ? `Edit ${editingReport.displayName}` : 'New Report'}
          </Typography>
          {reportId && (
            <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>Draft — not yet presented</Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8' }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>

        {/* Type selector — hidden for field employees (always FIELD_SERVICE) */}
        {!fromField && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
              Report Type
            </Typography>
            <ToggleButtonGroup
              value={type}
              exclusive
              onChange={(_, v) => v && setType(v)}
              size="small"
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 13, px: 2 } }}
            >
              <ToggleButton value="FIELD_SERVICE">Field Service</ToggleButton>
              <ToggleButton value="INTERNAL">Internal</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Filters */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>
              Filters
            </Typography>
            {(startDate || endDate || filterUserId || filterInvoiced !== 'all') && (
              <Button
                size="small"
                onClick={() => { setStartDate(null); setEndDate(null); setFilterUserId(''); setFilterInvoiced('all'); }}
                sx={{ color: '#94A3B8', fontSize: 11, textTransform: 'none', minWidth: 0, p: 0 }}
              >
                Clear all
              </Button>
            )}
          </Box>
          {/* Date range */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
            <DatePicker
              selected={startDate}
              onChange={d => setStartDate(d)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              placeholderText="From"
              dateFormat="dd.MM.yyyy"
              customInput={<TextField size="small" label="From" sx={{ width: 140 }} />}
            />
            <DatePicker
              selected={endDate}
              onChange={d => setEndDate(d)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              placeholderText="To"
              dateFormat="dd.MM.yyyy"
              customInput={<TextField size="small" label="To" sx={{ width: 140 }} />}
            />
          </Box>
          {/* Employee + invoiced row — invoiced filter hidden for field employees */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {fromField ? (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Show entries</InputLabel>
                <Select
                  value={filterUserId}
                  label="Show entries"
                  onChange={e => setFilterUserId(e.target.value)}
                >
                  <MenuItem value={String(currentUserId)}>My entries</MenuItem>
                  <MenuItem value="">All employees</MenuItem>
                </Select>
              </FormControl>
            ) : entryEmployees.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Employee</InputLabel>
                <Select
                  value={filterUserId}
                  label="Employee"
                  onChange={e => setFilterUserId(e.target.value)}
                >
                  <MenuItem value="">All employees</MenuItem>
                  {entryEmployees.map(u => (
                    <MenuItem key={u.id} value={String(u.id)}>
                      {u.firstName} {u.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {!fromField && (
              <ToggleButtonGroup
                value={filterInvoiced}
                exclusive
                onChange={(_, v) => v && setFilterInvoiced(v)}
                size="small"
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 12, px: 1.5, py: 0.5 } }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="no">Not invoiced</ToggleButton>
                <ToggleButton value="yes">Invoiced</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>
        </Box>

        {/* Entry filter search */}
        <TextField
          size="small"
          fullWidth
          placeholder="Search entries…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Entry checklist */}
        <Box sx={{ mb: 3 }}>
          <Section id="entries" items={filteredEntries} selectedIds={selectedEntryIds}
            onToggle={id => toggle(setSelectedEntryIds, id)}
            open={sectionsOpen.entries}
            onToggleOpen={() => setSectionsOpen(p => ({ ...p, entries: !p.entries }))}
            filter={filter} />
          <Section id="expenses" items={allExpenses} selectedIds={selectedExpenseIds}
            onToggle={id => toggle(setSelectedExpenseIds, id)}
            open={sectionsOpen.expenses}
            onToggleOpen={() => setSectionsOpen(p => ({ ...p, expenses: !p.expenses }))}
            filter={filter} hidePrice={fromField} />
          <Section id="subServices" items={allSubServices} selectedIds={selectedSubServiceIds}
            onToggle={id => toggle(setSelectedSubServiceIds, id)}
            open={sectionsOpen.subServices}
            onToggleOpen={() => setSectionsOpen(p => ({ ...p, subServices: !p.subServices }))}
            filter={filter} />
          <Section id="inventory" items={allInventory} selectedIds={selectedInventoryIds}
            onToggle={id => toggle(setSelectedInventoryIds, id)}
            open={sectionsOpen.inventory}
            onToggleOpen={() => setSectionsOpen(p => ({ ...p, inventory: !p.inventory }))}
            filter={filter} />
        </Box>

        {/* Work description */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
            Work Description
          </Typography>
          <TextField
            multiline
            rows={4}
            fullWidth
            size="small"
            placeholder="Describe the work performed…"
            value={workDescription}
            onChange={e => setWorkDescription(e.target.value)}
          />
        </Box>

        {/* Images */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
            Photos
          </Typography>

          {/* Upload zone */}
          <Box
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); handleImageUpload(e.dataTransfer.files); }}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: `2px dashed ${dragOver ? '#427898' : '#CBD5E1'}`,
              borderRadius: 2,
              p: 2.5,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: dragOver ? '#EFF6FF' : '#FAFBFC',
              mb: 2,
              transition: 'all 0.15s',
              '&:hover': { borderColor: '#427898', bgcolor: '#EFF6FF' },
            }}
          >
            {uploadingImage
              ? <CircularProgress size={20} sx={{ color: '#427898' }} />
              : <>
                  <AddPhotoAlternateIcon sx={{ fontSize: 28, color: '#94A3B8', mb: 0.5 }} />
                  <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>Click or drag photos here</Typography>
                  <Typography sx={{ fontSize: 11, color: '#CBD5E1' }}>Compressed automatically · max 1920px</Typography>
                </>
            }
          </Box>
          <input ref={fileInputRef} type="file" multiple accept="image/*" hidden
            onChange={e => handleImageUpload(e.target.files)} />

          {/* Image grid */}
          {images.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              {images.map(img => (
                <Box key={img.id} sx={{ border: '1px solid #EEF2F6', borderRadius: 1.5, overflow: 'hidden', bgcolor: '#fff' }}>
                  <Box sx={{ position: 'relative', height: 120, bgcolor: '#F8FAFC' }}>
                    <SecureImage
                      objectKey={img.objectKey}
                      style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteImage(img.id)}
                      sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: '#fff' }, p: 0.5 }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 15, color: '#EF4444' }} />
                    </IconButton>
                  </Box>
                  <Box sx={{ p: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Caption…"
                      value={img.caption || ''}
                      onChange={e => handleCaptionChange(img.id, e.target.value)}
                      variant="standard"
                      sx={{ '& .MuiInput-root': { fontSize: 12 } }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Sticky footer */}
      <Box sx={{ px: 3, py: 2, borderTop: '1px solid #EEF2F6', flexShrink: 0, display: 'flex', gap: 1.5, bgcolor: '#fff' }}>
        <Button variant="outlined" onClick={onClose} sx={{ color: '#64748B', borderColor: '#E2E8F0', textTransform: 'none', fontSize: 13 }}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={handleSaveDraft}
          disabled={saving}
          sx={{ textTransform: 'none', fontSize: 13, borderColor: '#E2E8F0', color: '#427898' }}
        >
          {saving ? <CircularProgress size={16} /> : 'Save Draft'}
        </Button>
        <Box sx={{ flex: 1 }} />
        {type === 'FIELD_SERVICE' ? (
          <Button
            variant="contained"
            onClick={handlePresentToCustomer}
            disabled={saving}
            sx={{ textTransform: 'none', fontSize: 13, bgcolor: '#427898', '&:hover': { bgcolor: '#355f79' }, fontWeight: 600 }}
          >
            Present to Customer →
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handlePublish}
            disabled={saving || !reportId}
            sx={{ textTransform: 'none', fontSize: 13, bgcolor: '#427898', '&:hover': { bgcolor: '#355f79' }, fontWeight: 600 }}
          >
            Publish
          </Button>
        )}
      </Box>
    </Drawer>
  );
}
